import { TemporalCoordinate, SpatialCoordinate, Datum } from './types'
import * as CryptoJS from 'crypto-js';

/**
 * compare two datums temporally (who comes first) 
 */
export const compareByTime = (dataItem0: TemporalCoordinate, dataItem1: TemporalCoordinate): number => {
  if (dataItem0 == undefined && dataItem1 == undefined) return 0;
  if (dataItem0 == undefined) return 1;
  if (dataItem1 == undefined) return -1;

  if (isMoment(dataItem0)) {
    if (isMoment(dataItem1)) {
      return dataItem0.time - dataItem1.time;
    } else {
      if (hasStart(dataItem1)) {
        return dataItem0.time - dataItem1.start;
      } else {
        return 1;
      }
    }
  } else {
    if (hasStart(dataItem0)) {
      if (isMoment(dataItem1)) {
        return dataItem0.start - dataItem1.time;
      } else {
        if (hasStart(dataItem1)) {
          return dataItem0.start - dataItem1.start;
        } else {
          return 1;
        }
      }
    } else {
      if (hasEnd(dataItem0)) {
        if (isUntil(dataItem1)) {
          return dataItem0.end - dataItem1.end;
        } else {
          if (isPerpetual(dataItem1)) {
            return 1;
          } else {
            return -1;
          }
        }
      } else {
        if (isPerpetual(dataItem1)) {
          return 0;
        } else {
          return -1;
        }
      }
    }
  }
}
/**
 * return a hash based on the id and the temporal properties of the datum
 */
export const datumTemporalHash = (datum: Datum) => {
  return datum.start !== undefined && datum. end!== undefined 
  ? CryptoJS.MD5(`${datum.id}_${datum.start}_${datum.end}`).toString(CryptoJS.enc.Hex)
  : CryptoJS.MD5(`${datum.id}_${datum.time}`).toString(CryptoJS.enc.Hex);
}
/**
 * check if given datum holds temporal properties
 */
export const isTemporal = (datum: any): boolean => {
  return datum.time !== undefined || datum.start !== undefined || datum.end !== undefined;
}
/**
 * check if given datum represents a single moment in time
 */
export const isMoment = (datum: any): boolean => {
  return isNum(datum.time);
}
/**
 * check if given datum represents a time finite range
 */
export const isRange = (datum: any): boolean => {
  return datum.time === undefined && isNum(datum.start) && isNum(datum.end) && datum.start < datum.end;
}
/**
 * check if given datum represents a time range that has no start nor end
 */
export const isPerpetual = (datum: any): boolean => {
  return datum.time === undefined && datum.start === null && datum.end === null;
}
/**
 * check if given datum represents a time range that has a start but no end
 */
export const isSince = (datum: any): boolean => {
  return datum.time === undefined && isNum(datum.start) && !isNum(datum.end);
}
/**
 * check if given datum represents a time range that has a no start but does have an end
 */
export const isUntil = (datum: any): boolean => {
  return datum.time === undefined && !isNum(datum.start) && isNum(datum.end);
}
/**
 * check if given datum has a start property that represents a moment in time
 */
export const hasStart = (datum: any): boolean => {
  return datum.time === undefined && isNum(datum.start);
}
/**
 * check if given datum has an end property that represents a moment in time
 */
export const hasEnd = (datum: any): boolean => {
  return datum.time === undefined && isNum(datum.end);
}
/**
 * returns the duration of the given datum
 */
export const duration = (datum: any): number => {
  return  isNum(datum.time)? 0 : isNum(datum.start) && isNum(datum.end) ? datum.end - datum.start : undefined;
}

/**
 * compare two datums spatially ( who's position / north-west corner lies more north-west) 
 */
export const compareByLocation = (dataItem0: SpatialCoordinate, dataItem1: SpatialCoordinate): number => {
  if (dataItem0 == undefined && dataItem1 == undefined) return 0;
  if (dataItem0 == undefined) return 1;
  if (dataItem1 == undefined) return -1;

  let lat0 = isNum(dataItem0.lat) ? dataItem0.lat : dataItem0.north;
  let lat1 = isNum(dataItem1.lat) ? dataItem1.lat : dataItem1.north;
  if (lat0 > lat1) { //datum 0 lies more north
    return -1;
  } else if (lat1 > lat0) { //datum 1 lies more north
    return 1;
  } else { //datum 0 and 1 have the same latitude
    let lng0 = isNum(dataItem0.lng) ? dataItem0.lng : dataItem0.west;
    let lng1 = isNum(dataItem1.lng) ? dataItem1.lng : dataItem1.west;

    if (lng0 < lng1) { //datum 0 lies more west
      return -1;
    } else if (lng1 < lng0) {//datum 1 lies more west
      return 1;
    } else { // datum 0 and 1 lie on the same position
      return 0;
    }
  }
}
/**
 * return a hash based on the id and the spatial properties of the datum
 */
export const datumSpatialHash = (datum: Datum) => {
  return CryptoJS.MD5(`${datum.id}_${datum.lat !==undefined ? datum.lat : datum.north}_${datum.lng !==undefined ? datum.lng : datum.west}`).toString(CryptoJS.enc.Hex);
}
/**
 * check if given datum holds spatial properties
 */
export const isSpatial = (datum: any): boolean => {
  return (isNum(datum.lat) && isNum(datum.lng)) ||
    (isNum(datum.north) && isNum(datum.east) &&
      isNum(datum.south) && isNum(datum.west));
}
/**
 * check if given datum represents a single point in space
 */
export const isPoint = (datum: any): boolean => {
  return (isNum(datum.north) && isNum(datum.east) && isNum(datum.south) && isNum(datum.west))
    ? (Math.abs(datum.south - datum.north) < 0.00001) && (Math.abs(datum.west - datum.east) < 0.00001)
    : isNum(datum.lat) && isNum(datum.lng);
}
/**
 * check if given datum represents an area in space
 */
export const isArea = (datum: any): boolean => {
  return (isNum(datum.north) && isNum(datum.east) && isNum(datum.south) && isNum(datum.west)) &&
    (Math.abs(datum.south - datum.north) >= 0.00001) || (Math.abs(datum.west - datum.east) >= 0.00001);
}
export const isVersional = (datum: any): boolean => {
  return isNum(datum.version);
}

const isNum = ( num: any ): boolean  => typeof num === 'number';
